import type { FC, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Pressable, xcss } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';
import Tooltip from '@atlaskit/tooltip';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	pauseButtonLabel: {
		id: 'audio.play-pause-button.pause-label',
		defaultMessage: 'Pause',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will pause the actively playing audio',
	},
	playButtonLabel: {
		id: 'audio.play-pause-button.play-label',
		defaultMessage: 'Play',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will play the actively paused audio',
	},
	loadingButtonLabel: {
		id: 'audio.play-pause-button.loading-label',
		defaultMessage: 'Loading',
		description:
			'The label is used as a tooltip and for accessibility for the icon button when the audio is loading',
	},
});

export type PlayPauseButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
	isLoading: boolean;
	hasFetched?: boolean;
	fetchContent?: () => void;
};

const buttonStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	width: '40px',
	height: '40px',
	borderRadius: 'border.radius.circle',
	border: '1px solid',
	borderColor: 'color.border',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export const PlayPauseButton: FC<PlayPauseButtonProps> = ({
	audioRef,
	isPlaying,
	isLoading,
	hasFetched = true,
	fetchContent,
}) => {
	const { formatMessage } = useIntl();
	const pressableRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (pressableRef.current) {
			pressableRef.current.focus();
		}
	}, []);

	let labelValue: string;
	let buttonIcon: ReactNode;

	if (isLoading) {
		labelValue = formatMessage(i18n.loadingButtonLabel);
		buttonIcon = <Spinner label={labelValue} testId="miniplayer-loading" />;
	} else if (isPlaying) {
		labelValue = formatMessage(i18n.pauseButtonLabel);
		buttonIcon = (
			<VideoPauseOverlayIcon
				color={token('color.icon.selected')}
				label=""
				testId="miniplayer-pause"
			/>
		);
	} else {
		labelValue = formatMessage(i18n.playButtonLabel);
		buttonIcon = (
			<VideoPlayOverlayIcon
				color={token('color.icon.selected')}
				label=""
				testId="miniplayer-play"
			/>
		);
	}

	const handlePause = () => {
		if (audioRef.current) {
			audioRef.current.pause();
		}
	};

	const handlePlay = async () => {
		if (audioRef.current && hasFetched) {
			await audioRef.current.play();
		} else {
			fetchContent?.();
		}
	};

	return (
		<Tooltip content={labelValue}>
			<Pressable
				xcss={buttonStyles}
				onClick={isPlaying ? handlePause : handlePlay}
				componentName="PlayPauseButton"
				ref={pressableRef}
				isDisabled={isLoading}
			>
				{buttonIcon}
				<VisuallyHidden>{labelValue}</VisuallyHidden>
			</Pressable>
		</Tooltip>
	);
};
